/*
 * The widget frame's document — ONE definition, used by the host and by every
 * harness. A second copy of a security header drifts from the first, and the
 * drift is invisible because both sides still render.
 */

/**
 * The frame's Content-Security-Policy.
 *
 * `default-src 'none'` is the load-bearing clause: it is what makes fetch, XHR,
 * WebSocket, and the image-beacon channel all fail — measured, see
 * tools/sandbox-probe/RESULTS.md. That beacon is the channel EchoLeak
 * (CVE-2025-32711) and AgentFlayer both used, and closing it is most of the
 * value of this frame.
 *
 * `'unsafe-eval'` is here deliberately, and it is the one clause that looks
 * alarming, so: a widget IS a program, and compiling one is the point. The
 * reason eval is normally dangerous is that it turns an injection into
 * exfiltration or privilege escalation — and in this frame there is nothing to
 * escalate to and nowhere to exfiltrate to. No credentials (the host holds
 * them), no storage (opaque origin has none), no parent DOM (cross-origin), no
 * network (this policy). Refusing eval here would not make widget code safer;
 * it would only force the same code through a slower interpreter shipped inside
 * the frame. This matches what Claude Artifacts serves for the same reason.
 *
 * The frame's own script cannot remove this meta CSP — verified in the probe.
 *
 * NOT present, and deliberately: `connect-src` (covered by default-src),
 * `img-src` (ditto — a widget draws with vectors, not remote images), and any
 * host allowlist at all. An allowlist entry is a capability, and every one of
 * the 2025-26 agent exfiltration incidents went through a permitted destination
 * rather than through a broken sandbox.
 */
export const FRAME_CSP =
  "default-src 'none'; " +
  "script-src 'unsafe-inline' 'unsafe-eval' blob:; " +
  "style-src 'unsafe-inline'; " +
  "font-src data:; " +
  "worker-src blob:; " +
  "form-action 'none'; " +
  "base-uri 'none'";

/**
 * The sandbox token set. `allow-scripts` WITHOUT `allow-same-origin` is what
 * yields an opaque origin — and the combination everyone warns about
 * (allow-scripts + allow-same-origin, letting a frame strip its own sandbox
 * attribute) needs the frame to be same-origin with its embedder, which this
 * never is. That is why no second origin is required here.
 */
export const FRAME_SANDBOX = 'allow-scripts';

/** Build the frame's srcdoc around the bundled frame script. */
export function frameDoc(frameJs: string): string {
  return (
    '<!doctype html><meta charset="utf-8">' +
    `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">` +
    // The app's own type inside the frame: a separate document inherits no font, and a widget
    // that renders in the browser default reads as foreign. Tabular digits so a live value does
    // not wobble as it ticks.
    '<style>html,body{margin:0;height:100%;background:transparent;overflow:hidden;' +
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
    'font-variant-numeric:tabular-nums}' +
    // A scene smaller than the tile sits in its middle rather than in its top-left corner:
    // a widget that draws a fixed-size picture is placed by the frame, not by the author.
    '#root{height:100%;width:100%;display:flex;align-items:center;justify-content:center}' +
    '#root>svg,#root>div{max-width:100%;max-height:100%}' +
    // The readout chip: the app's .vg-sparkline-tooltip, in the tokens the host handed over.
    '.wg-tip{position:fixed;z-index:10;pointer-events:none;display:flex;flex-direction:column;gap:2px;' +
    'background:var(--card-bg);border:1px solid var(--border-color);box-shadow:0 2px 8px rgba(0,0,0,0.15);' +
    'border-radius:6px;padding:5px 10px;' +
    'font-size:11px;color:var(--text-primary);white-space:nowrap}' +
    '.wg-tip-row{display:flex;align-items:center;gap:6px}' +
    '.wg-tip-swatch{width:8px;height:3px;border-radius:1.5px;flex:0 0 auto}' +
    '.wg-tip-label{color:var(--text-secondary)}' +
    '.wg-tip-value{font-weight:500;font-variant-numeric:tabular-nums}' +
    '.wg-tip-time{color:var(--text-tertiary);font-size:10px}</style>' +
    '<div id="root"></div><script>' +
    // A literal </script> inside the payload would terminate this tag; a
    // minified bundle can legitimately contain one inside a string.
    frameJs.replace(/<\/script/gi, '<\\/script') +
    '</script>'
  );
}
